import React, { useState } from "react";
import { Form } from "react-bootstrap";

import Button from "components/Button";
import type { PaychexWorkerRecord } from "types/models/paychexWorkerRecord";

interface WorkerRecordPickerProps {
  workerRecords: PaychexWorkerRecord[];
  onSelect: (workerRecord: PaychexWorkerRecord) => void;
  // When embedded in a modal the modal header owns the question, so only the
  // explanatory subtext is rendered here.
  hideHeading?: boolean;
}

const SUBTEXT =
  "We found more than one employer on your Paychex account. Choose which " +
  "paycheck you'd like to contribute from.";

// Presentational: lets a Paychex person with more than one worker/employment
// record choose which employer's paycheck to deduct from. The selected record's
// payroll frequency drives the downstream contribution schedule.
const WorkerRecordPicker = ({
  workerRecords,
  onSelect,
  hideHeading = false,
}: WorkerRecordPickerProps): React.JSX.Element => {
  const [selectedId, setSelectedId] = useState("");

  const handleNext = () => {
    const record = workerRecords.find((r) => r.id === selectedId);
    if (record) {
      onSelect(record);
    }
  };

  return (
    <div id="worker-record-picker">
      {hideHeading ? (
        <p>{SUBTEXT}</p>
      ) : (
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">
              Which employer would you like to deduct from?
            </h1>
            <p className="page-subtext">{SUBTEXT}</p>
          </article>
        </section>
      )}
      <Form.Group
        className="mb-3"
        controlId="workerRecordSelect"
        style={{ maxWidth: 360 }}
      >
        <Form.Label>Employer</Form.Label>
        <Form.Select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          <option value="" disabled>
            Select an employer
          </option>
          {workerRecords.map((record) => {
            // The list query leaves payrollFrequency null (it's resolved
            // per-worker on selection), so label by employer + displayId here.
            return (
              <option value={record.id} key={record.id}>
                {record.employerName}
                {record.displayId ? ` (${record.displayId})` : ""}
              </option>
            );
          })}
        </Form.Select>
      </Form.Group>
      <div className="submit-row">
        <Button
          name="submit"
          btnLabel="Next"
          withArrow
          disabled={!selectedId}
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

export default WorkerRecordPicker;
